/**
 * Shared intake-field logic + inputs (Workspace v2 Slice 1).
 *
 * A config-list dropdown that reveals a free-text box when the tenant's "Other"
 * option is picked (intake_call_by). The trigger label (freeTextLabel) comes from
 * config (intake_list_free_text), never hardcoded (§3), and is matched by VALUE
 * not list position. An existing off-list value (e.g. a previously-typed custom
 * name) opens in free-text mode automatically. The stored value is the chosen
 * name OR the typed custom name — never the sentinel label itself (soft-validated
 * server-side; off-list accepted).
 *
 *   * useOtherableSelect — the show/hide + value-mapping logic. The "+ New intake"
 *     form calls this directly so its <select> matches the form's styling.
 *   * OtherableSelect — a drawer-styled component (sibling of the drawer's
 *     IntakeSelect) wrapping the hook. Mount it with a `key` tied to the record id
 *     so the free-text toggle resets when switching records (the value loads async).
 */

import { useState, type ReactNode } from 'react';
import { LABEL_COL } from './drawer/drawerLayout';
import { SelectField } from '@/components/primitives/SelectField';

export interface OtherableSelectState {
  /** What the <select> should show (value if on-list, the sentinel if in free-text mode, else ''). */
  selectValue: string;
  /** Whether to render the free-text <input>. */
  showOther: boolean;
  /** Seed value for an uncontrolled free-text <input> (a prior custom value, else ''). */
  initialOther: string;
  /** Wire to the <select> onChange (pass e.target.value). */
  onSelectChange: (v: string) => void;
  /** Wire to the free-text <input> onChange (pass e.target.value). */
  onTextChange: (v: string) => void;
}

export function useOtherableSelect(
  value: string | null,
  options: string[],
  freeTextLabel: string | undefined,
  onChange: (v: string | null) => void,
): OtherableSelectState {
  const onList = value != null && value !== '' && options.includes(value);
  const isCustomInitial = !!freeTextLabel && value != null && value !== '' && !onList;
  const [showOther, setShowOther] = useState(isCustomInitial);

  return {
    selectValue: showOther ? (freeTextLabel ?? '') : (onList ? (value as string) : ''),
    showOther,
    initialOther: isCustomInitial ? (value as string) : '',
    onSelectChange: (v) => {
      if (freeTextLabel && v === freeTextLabel) {
        setShowOther(true); // reveal the box; keep any existing custom value until they type
      } else {
        setShowOther(false);
        onChange(v || null);
      }
    },
    onTextChange: (v) => onChange(v || null),
  };
}

/** Drawer-styled config-list select with the "Other" → free-text reveal. Key it by record id.
 *  `layout` (design pass 2026-07-25): 'stacked' is the original label-above-control shape;
 *  'row' puts the field on the drawer's ONE label:value grid. Same wiring, same testids. */
export function OtherableSelect({ label, value, options, freeTextLabel, onChange, testId, layout = 'stacked' }: {
  label: string;
  value: string | null;
  options: string[];
  freeTextLabel?: string;
  onChange: (v: string | null) => void;
  testId?: string;
  layout?: 'stacked' | 'row';
}): ReactNode {
  const f = useOtherableSelect(value, options, freeTextLabel, onChange);
  const isRow = layout === 'row';
  const control =
    'w-full rounded-base border border-sage-200 bg-paper px-2 py-1 text-[13px] text-ink ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400';
  return (
    <label
      className={
        isRow
          ? `${LABEL_COL} py-1 text-[13px]`
          : 'flex flex-col text-sage-400'
      }
    >
      <span className={isRow ? 'pt-1.5 text-sage-400' : undefined}>{label}</span>
      <span className={isRow ? 'min-w-0' : 'contents'}>
        <SelectField size="compact" value={f.selectValue} aria-label={label} data-testid={testId ? `${testId}-select` : undefined}
          onChange={(e) => f.onSelectChange(e.target.value)}
          className="w-full">
          <option value="">Select</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </SelectField>
        {f.showOther && (
          <input defaultValue={f.initialOther} aria-label={`${label}, specify`} placeholder="Type a name"
            data-testid={testId ? `${testId}-other` : undefined}
            onChange={(e) => f.onTextChange(e.target.value)}
            className={isRow ? control + ' mt-1' : 'mt-1 rounded-base border border-sage-200 px-2 py-1 text-ink'} />
        )}
      </span>
    </label>
  );
}
